import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import AudioRecorder from '../AudioRecorder';
import TranscriptList from '../TranscriptList';
import TranscriptDetail from '../TranscriptDetail';
import Chat from '../Chat';
import ClientsPage from '../ClientsPage';
import TasksPageRefactored from '../../pages/TasksPageRefactored';
import NotebookPage from '../../pages/NotebookPage';
import VonChat from '../VonChat';

// Industry configuration for the toggle
const INDUSTRY_OPTIONS = [
  { value: 'financial_services', label: 'Financial Services', icon: '💼' },
  { value: 'healthcare', label: 'Healthcare', icon: '🏥' },
  { value: 'sales', label: 'Sales & Marketing', icon: '📈' },
  { value: 'real_estate', label: 'Real Estate', icon: '🏠' },
  { value: 'legal', label: 'Legal Services', icon: '⚖️' },
  { value: 'hr', label: 'Human Resources', icon: '👥' },
  { value: 'other', label: 'Other', icon: '🔧' },
];

interface Transcript {
  id: string;
  title: string;
  content: string;
  preview?: string;
  audio_metadata: {
    r2_key?: string;
    original_filename?: string;
    mime_type?: string;
    file_size_bytes?: number;
  };
  audio_duration_formatted?: string;
  created_at: string;
  updated_at: string;
  industry_context?: string;
  analysis_type?: string;
}

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { logout, user } = useAuth();
  
  const [selectedTranscript, setSelectedTranscript] = useState<Transcript | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // Industry toggle state - simplified to just enable/disable
  const [useIndustryAnalysis, setUseIndustryAnalysis] = useState<boolean>(true);
  const [selectedIndustry, setSelectedIndustry] = useState<string>(user?.industry || 'financial_services');
  const [showStatusMessage, setShowStatusMessage] = useState<boolean>(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounce search query to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Determine current view from location
  const getCurrentView = (): 'transcripts' | 'clients' | 'tasks' | 'von-chat' | 'client-intelligence' | 'notemann' => {
    if (location.pathname === '/tasks') return 'tasks';
    if (location.pathname === '/clients') return 'clients';
    if (location.pathname === '/client-intelligence') return 'client-intelligence';
    if (location.pathname === '/notemann') return 'notemann';
    if (location.pathname === '/von-chat') return 'von-chat';
    return 'transcripts';
  };

  const currentView = getCurrentView();

  // Handle transcript selection from URL
  useEffect(() => {
    if (params.id && transcripts.length > 0) {
      const transcript = transcripts.find(t => t.id === params.id);
      if (transcript) {
        setSelectedTranscript(transcript);
      }
    } else if (location.pathname === '/' && selectedTranscript) {
      // Clear selection when going back to home without transcript ID
      setSelectedTranscript(null);
    }
  }, [params.id, transcripts, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleTranscriptionComplete = (result: string, savedTranscript?: Transcript) => {
    // If transcript was saved, trigger refresh and select it
    if (savedTranscript) {
      setRefreshTrigger(prev => prev + 1);
      setSelectedTranscript(savedTranscript);
      navigate(`/transcript/${savedTranscript.id}`);
    }
  };

  const handleSelectTranscript = (transcript: Transcript) => {
    setSelectedTranscript(transcript);
    navigate(`/transcript/${transcript.id}`);
  };

  // Handle transcript selection from chat by ID
  const handleSelectTranscriptById = async (transcriptId: string) => {
    // First, try to find the transcript in the current list
    const existingTranscript = transcripts.find(t => t.id === transcriptId);
    if (existingTranscript) {
      setSelectedTranscript(existingTranscript);
      navigate(`/transcript/${transcriptId}`);
      return;
    }

    // If not found, fetch it specifically by ID
    try {
      const response = await fetch(`/api/transcripts/${transcriptId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json() as { 
          transcript: Transcript;
          error?: string;
        };
        
        if (result.transcript) {
          // Format the transcript to match the expected structure
          const formattedTranscript: Transcript = {
            ...result.transcript,
            preview: result.transcript.content.substring(0, 100) + (result.transcript.content.length > 100 ? '...' : ''),
            audio_metadata: result.transcript.audio_metadata || {}
          };
          
          setSelectedTranscript(formattedTranscript);
          navigate(`/transcript/${transcriptId}`);
        } else {
          console.error('Transcript not found in response');
        }
      } else {
        console.error('Failed to fetch transcript:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch transcript:', error);
    }
  };

  const handleTranscriptsLoaded = (loadedTranscripts: Transcript[]) => {
    setTranscripts(loadedTranscripts);
  };

  // Handle transcript title updates
  const handleTitleUpdate = (newTitle: string) => {
    if (selectedTranscript) {
      const updatedTranscript = { ...selectedTranscript, title: newTitle };
      setSelectedTranscript(updatedTranscript);
      
      // Update the transcript in the transcripts list
      setTranscripts(prev => 
        prev.map(t => t.id === selectedTranscript.id ? updatedTranscript : t)
      );
    }
  };

  // Handle transcript deletion
  const handleTranscriptDelete = (deletedTranscriptId: string) => {
    // Remove from transcripts list
    setTranscripts(prev => prev.filter(t => t.id !== deletedTranscriptId));
    
    // Clear selected transcript if it was the deleted one
    if (selectedTranscript?.id === deletedTranscriptId) {
      setSelectedTranscript(null);
      navigate('/');
    }
    
    // Trigger refresh to ensure list is up to date
    setRefreshTrigger(prev => prev + 1);
  };

  // Update selected industry when user changes (keep for backend compatibility)
  useEffect(() => {
    if (user?.industry) {
      setSelectedIndustry(user.industry);
    }
  }, [user?.industry]);

  // Handle Turbo Intelligence toggle with brief status message
  const handleToggleChange = (enabled: boolean) => {
    setUseIndustryAnalysis(enabled);
    setShowStatusMessage(true);
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setShowStatusMessage(false);
    }, 3000);
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header with Navigation */}
      <header className="bg-white border-b border-gray-200 px-4 py-2 flex justify-between items-center">
        <a 
          href="/"
          className="text-xl font-semibold text-accent-purple hover:text-accent-purple-dark transition-colors cursor-pointer no-underline"
        >
          AddWise AI
        </a>
        <div className="flex items-center space-x-4">
          {/* Navigation Links */}
          <button
            onClick={() => navigate('/')}
            className={`p-2 rounded-lg transition-colors ${
              currentView === 'transcripts' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title="Home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10M9 21h6" />
            </svg>
          </button>

          <button
            onClick={() => navigate('/tasks')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentView === 'tasks' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Tasks
          </button>

          <button
            onClick={() => navigate('/von-chat')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentView === 'von-chat' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Von Chat
          </button>

          <button
            onClick={() => navigate('/notemann')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentView === 'notemann' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            NoteMann
          </button>

          <button
            onClick={() => navigate('/clients')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentView === 'clients' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Clients
          </button>

          <button
            onClick={() => navigate('/client-intelligence')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentView === 'client-intelligence' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Client Concierge
          </button>
          
          <div className="w-px h-6 bg-gray-300"></div>
          
          {/* Profile Dropdown */}
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              title="Profile"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showProfileDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    navigate('/settings');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </button>
                <div className="border-t border-gray-100"></div>
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    handleLogout();
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      {currentView === 'clients' ? (
        <div className="flex-1 overflow-hidden">
          <ClientsPage />
        </div>
      ) : currentView === 'tasks' ? (
        <div className="flex-1 overflow-auto">
          <TasksPageRefactored />
        </div>
              ) : currentView === 'notemann' ? (
        <NotebookPage />
      ) : currentView === 'von-chat' ? (
        <VonChat />
      ) : (
      <div className="flex flex-1 overflow-hidden">
        {/* Left Column - Transcript List */}
        <div className="w-1/4 bg-white border-r border-gray-200 p-4 flex flex-col">
            <div className="mb-4">
              <h2 className="text-xl font-semibold">Transcripts</h2>
            </div>
            
            {/* Turbo Intelligence Toggle Section */}
            <div className="mb-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Turbo Intelligence</span>
                  {user?.industry && (
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      <span>{INDUSTRY_OPTIONS.find(opt => opt.value === user.industry)?.icon}</span>
                      <span>{INDUSTRY_OPTIONS.find(opt => opt.value === user.industry)?.label}</span>
                    </div>
                  )}
                </div>
                
                {/* Enhanced Toggle Switch */}
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useIndustryAnalysis}
                    onChange={(e) => handleToggleChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className={`relative w-14 h-7 rounded-full peer transition-all duration-300 ease-in-out ${
                    useIndustryAnalysis 
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-200' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}>
                    <div className={`absolute top-0.5 left-0.5 bg-white rounded-full h-6 w-6 transition-all duration-300 ease-in-out shadow-md ${
                      useIndustryAnalysis ? 'transform translate-x-7' : 'transform translate-x-0'
                    }`}>
                      {/* Icon inside the toggle */}
                      <div className="flex items-center justify-center h-full w-full">
                        {useIndustryAnalysis ? (
                          <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Toggle Status Text */}
                  <span className={`ml-3 text-xs font-medium ${
                    useIndustryAnalysis ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {useIndustryAnalysis ? 'ON' : 'OFF'}
                  </span>
                </label>
              </div>
              
              {/* Status Messages */}
              {showStatusMessage && !useIndustryAnalysis && (
                <div className="text-xs text-amber-600 bg-amber-50 px-3 py-2 mt-2 rounded-md border border-amber-200 transition-opacity duration-300">
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>Smart Analysis (universal AI models)</span>
                  </div>
                </div>
              )}
              
              {showStatusMessage && useIndustryAnalysis && user?.industry && (
                <div className="text-xs text-green-600 bg-green-50 px-3 py-2 mt-2 rounded-md border border-green-200 transition-opacity duration-300">
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Turbo Intelligence: Specialized {INDUSTRY_OPTIONS.find(opt => opt.value === user.industry)?.label} AI</span>
                  </div>
                </div>
              )}
            </div>

            {/* Audio Recorder Section (now includes manual transcript) */}
            <div className="mb-4">
            <AudioRecorder 
              onComplete={handleTranscriptionComplete}
              industryContext={useIndustryAnalysis ? user?.industry : null}
              isIndustryAnalysisEnabled={useIndustryAnalysis}
            />
          </div>

            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search transcripts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-purple focus:border-transparent bg-white"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Transcript List Section - Expanded */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <TranscriptList 
                onSelectTranscript={handleSelectTranscript}
                selectedTranscriptId={selectedTranscript?.id}
                refreshTrigger={refreshTrigger}
                onTranscriptsLoaded={handleTranscriptsLoaded}
                searchQuery={debouncedSearchQuery}
              />
            </div>
        </div>

        {/* Middle Column - Transcript Details */}
        <div className="w-1/2 bg-[#fafafa] border-r border-gray-200 p-4">
          <h2 className="text-xl font-semibold mb-4">Details</h2>
            <TranscriptDetail 
              transcript={selectedTranscript} 
              onTitleUpdate={handleTitleUpdate}
              onTranscriptDelete={handleTranscriptDelete}
            />
        </div>

        {/* Right Column - AI Chat */}
        <div className="w-1/4 bg-[#f8f8f8] p-4">
          <h2 className="text-xl font-semibold mb-4">AI Chat</h2>
            <Chat 
              selectedTranscriptId={selectedTranscript?.id}
              onSelectTranscript={handleSelectTranscriptById}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout; 
